/**
 * Enrichissement de la page collection (§7.3).
 *
 * Tout fonctionne déjà sans JavaScript : filtres et tri sont un formulaire GET,
 * le tiroir de filtres est un <details> natif. Ce script ajoute uniquement le
 * confort :
 *   - le tiroir passe en mode off-canvas (attribut `enhanced` → voir CSS) ;
 *   - fermeture par Échap, par clic sur le fond assombri et par le bouton × ;
 *   - piège de focus dans le panneau (Tab boucle) ;
 *   - le tri s'applique au changement (le bouton « Trier » de repli est masqué).
 */
class CollectionFilters extends HTMLElement {
  #facets = null;
  #summary = null;

  connectedCallback() {
    this.#setupSort();
    this.#setupFacets();
  }

  /* -------- Tri : auto-envoi, repli sans JS masqué -------- */
  #setupSort() {
    const select = this.querySelector('[data-sort]');
    if (!select) return;

    this.querySelector('[data-sort-submit]')?.setAttribute('hidden', '');
    /* ⚠️ querySelector et NON closest : le <form> est un ENFANT de <collection-filters>,
       pas un ancêtre (voir sections/collection.liquid et sections/search.liquid, où la
       balise ouvre AVANT le formulaire). `closest('form')` remontait donc la chaîne des
       parents et renvoyait toujours `null` : l'auto-envoi ne partait jamais, et comme la
       ligne au-dessus masque le bouton « Trier » de secours, CHOISIR UN TRI NE FAISAIT
       RIEN DU TOUT. Mesuré au vrai Chrome le 2026-08-22 sur le thème de `main` : le
       sélecteur changeait d'affichage, l'URL restait nue, l'ordre des produits inchangé.
       Le défaut valait pour les 2 rayons à barre d'outils ET pour la page de résultats. */
    select.addEventListener('change', () => this.querySelector('form')?.submit());
  }

  /* -------- Tiroir de filtres -------- */
  #setupFacets() {
    this.#facets = this.querySelector('[data-facets]');
    if (!this.#facets) return;

    this.#summary = this.#facets.querySelector('summary');
    this.#facets.setAttribute('enhanced', '');

    this.#facets.addEventListener('toggle', () => {
      this.#summary?.setAttribute('aria-expanded', this.#facets.open ? 'true' : 'false');
      if (this.#facets.open && !this.#isInline()) this.#focusFirst();
    });

    this.querySelector('[data-facets-close]')?.addEventListener('click', () => this.#close());

    // Clic sur le fond assombri (le ::before couvre le <details> ouvert)
    this.#facets.addEventListener('click', (event) => {
      if (event.target === this.#facets) this.#close();
    });

    this.#facets.addEventListener('keydown', (event) => {
      if (!this.#facets.open || this.#isInline()) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.#close();
      } else if (event.key === 'Tab') {
        this.#trapFocus(event);
      }
    });

    window.addEventListener('pageshow', () => this.#close());

    // Option A : filtres toujours en tiroir off-canvas (bouton « Filtrer »), desktop
    // compris → plus de colonne latérale, la grille prend toute la largeur (4 colonnes).
    this.#applyMode();
  }

  #isInline() {
    // Plus jamais de mode « colonne permanente » (Option A) : toujours le tiroir.
    return false;
  }

  #applyMode() {
    if (!this.#facets) return;
    // Tiroir off-canvas à toutes les largeurs (fermé par défaut ; le verrou de
    // défilement ne s'active que quand le <details> est ouvert).
    this.#facets.setAttribute('scroll-lock', '');
    this.#facets.removeAttribute('inline');
    this.#facets.open = false;
    this.#summary?.setAttribute('aria-expanded', 'false');
  }

  #close() {
    if (this.#isInline()) return; // en colonne (ordinateur), rien à fermer
    if (!this.#facets?.open) return;
    this.#facets.open = false;
    this.#summary?.focus();
  }

  #focusFirst() {
    const panel = this.#facets.querySelector('.facets__panel');
    panel?.querySelector('button, [href], input:not([type="hidden"]), select')?.focus();
  }

  #trapFocus(event) {
    const panel = this.#facets.querySelector('.facets__panel');
    const focusable = panel?.querySelectorAll(
      'a[href], button:not([disabled]), select, input:not([type="hidden"]):not([disabled])'
    );
    if (!focusable || focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get('collection-filters')) {
  customElements.define('collection-filters', CollectionFilters);
}

/* ⛔ NE PAS RÉINTRODUIRE : « Quelle version choisir ? » ouvert automatiquement en desktop.
 *
 * Livré par la PR #377 le 2026-08-21, puis RETIRÉ le même jour après que le marchand a vu la
 * page en vrai : « ça il ne faut pas qu'il soit ouvert ici, parce que là on ne voit plus rien
 * d'un coup, on ne voit plus les produits. Il faut que ce soit cliquable et que ça s'ouvre
 * quand la personne l'ouvre elle-même. »
 *
 * Pourquoi mes mesures ne l'avaient PAS vu venir, et c'est la leçon à retenir : je m'étais
 * arrêté au critère « les produits restent-ils au-dessus du pli ? ». Ils y restaient (802 px
 * pour un pli de 900 à 1440×900), donc j'avais conclu que c'était bon. Mais un produit dont
 * SEUL LE HAUT dépasse n'est pas un produit « visible » : entre l'intro, les bulles de rebond
 * et un accordéon de 419 px, le premier écran ne montrait plus de marchandise. Un seuil
 * respecté ne vaut pas une page qui se tient — seul le regard tranche cela.
 *
 * L'accordéon reste donc un <details> REPLIÉ que le visiteur ouvre lui-même, comme sur la
 * fiche produit. Le récit « Qu'est-ce que le citron noir ? », lui, est placé APRÈS la grille :
 * il explique sans jamais coûter un pixel au premier écran.
 */
